<script setup lang="ts">
import type { Submenu } from '@global-types/nav/menuItems';
import { useNavbarStore } from '@stores/layout/useNavbarStore';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import IconMagnifyingGlass from '@icons/ph_magnifying-glass.svg';
import IconEnvelope from '@icons/ph_envelope.svg';
import IconHeartStraight from '@icons/ph_heart-straight.svg';
import IconAlignTop from '@icons/ph_align-top.svg';
import IconRent from '@icons/ph_rent.svg';
import IconBriefCase from '@icons/ph_briefcase.svg';
import Icon from '@components/Icon.vue';
import Divider from '@components/Divider.vue';
import MenuLink from '@app/layout/nav/desktop/components/MenuLink.vue';
import MenuItemGroup from '@app/layout/nav/desktop/components/MenuItemGroup.vue';
import { trans } from '@tools/translation.ts';
import {
  URL_BUYER_INVOICES,
  URL_SEARCH,
  URL_SELECTION_ITEM_DASHBOARD,
  URL_USER_INBOX,
  URL_USER_ORGANIZATION_MANAGE, URL_USER_ORGANIZATION_PAYMENT_METHOD_BUYER,
  URL_USER_ORGANIZATION_TEAM,
  URL_USER_PROPOSAL,
} from '@tools/routes/routes';
import MenuAccountManagerContact from '@app/layout/nav/MenuAccountManagerContact.vue';
import { useUserProfileStore } from '@stores/useUserProfileStore.ts';
import { MenuItemGroup as MenuItemGroupEnum } from '@enums/menuItemGroups';
import MenuList from '@app/layout/nav/desktop/components/MenuList.vue';

const navbarStore = useNavbarStore();
const { isExpanded, notifications } = storeToRefs(navbarStore);
const { isCurrentDropdownOpen, setCurrentDropdownOpen } = navbarStore;

const { user, hasPayBuyer } = storeToRefs(useUserProfileStore());

const subMenuOrganization = computed<Submenu[]>(() => {
  const items = [
    { name: trans('user_menu.organization'), link: URL_USER_ORGANIZATION_MANAGE },
    { name: trans('user_menu.team'), link: URL_USER_ORGANIZATION_TEAM },
  ];

  if (hasPayBuyer.value) {
    items.push(
      { name: trans('user_menu.gestion_parameters'), link: URL_USER_ORGANIZATION_PAYMENT_METHOD_BUYER },
    );
  }

  return items;
});
</script>

<template>
  <MenuList>
    <li>
      <MenuLink
        :link="URL_SEARCH"
        :is-expanded="isExpanded"
      >
        <template #icon="{ isActive }">
          <Icon
            :svg="IconMagnifyingGlass"
            width="24"
            height="24"
            :fill="isActive ? 'black' : 'gray-darker'"
          />
        </template>

        <template #text>
          {{ trans('user_menu.search') }}
        </template>
      </MenuLink>
    </li>

    <li>
      <MenuLink
        :link="URL_USER_INBOX"
        :is-expanded="isExpanded"
        :has-pin="notifications.unread_messages > 0"
      >
        <template #icon="{ isActive }">
          <Icon
            :svg="IconEnvelope"
            width="24"
            height="24"
            :fill="isActive ? 'black' : 'gray-darker'"
          />
        </template>

        <template #text>
          {{ trans('user_menu.inbox') }}

          <template v-if="notifications.unread_messages > 0">
            ({{ notifications.unread_messages }})
          </template>
        </template>
      </MenuLink>
    </li>

    <li>
      <MenuLink
        :link="URL_USER_PROPOSAL"
        :is-expanded="isExpanded"
        :has-pin="notifications.unread_match_proposal > 0"
      >
        <template #icon="{ isActive }">
          <Icon
            :svg="IconHeartStraight"
            width="24"
            height="24"
            :fill="isActive ? 'black' : 'gray-darker'"
          />
        </template>

        <template #text>
          {{ trans('user_menu.selection_item') }}

          <template v-if="notifications.unread_match_proposal > 0">
            ({{ notifications.unread_match_proposal }})
          </template>
        </template>
      </MenuLink>
    </li>

    <li>
      <MenuLink
        :link="URL_SELECTION_ITEM_DASHBOARD"
        :is-expanded="isExpanded"
      >
        <template #icon="{ isActive }">
          <Icon
            :svg="IconAlignTop"
            width="24"
            height="24"
            :fill="isActive ? 'black' : 'gray-darker'"
          />
        </template>

        <template #text>
          {{ trans('user_menu.deals') }}
        </template>
      </MenuLink>
    </li>

    <li class="px-3">
      <Divider class="m-0" color="gray" />
    </li>

    <li>
      <MenuLink
        :link="URL_BUYER_INVOICES"
        :is-expanded="isExpanded"
      >
        <template #icon="{ isActive }">
          <Icon
            :svg="IconRent"
            width="24"
            height="24"
            :stroke="isActive ? 'black' : 'gray-darker'"
          />
        </template>

        <template #text>
          {{ trans('user_menu.invoices') }}
        </template>
      </MenuLink>
    </li>

    <li class="px-3">
      <Divider class="m-0" color="gray" />
    </li>

    <li>
      <MenuItemGroup
        :is-expanded="isExpanded"
        :is-dropdown-open="isCurrentDropdownOpen(MenuItemGroupEnum.MY_ORGANIZATION)"
        :submenus="subMenuOrganization"
        @toggle-dropdown="(isOpen) => setCurrentDropdownOpen(isOpen ? MenuItemGroupEnum.MY_ORGANIZATION : null)"
      >
        <template #icon="{ isActive }">
          <Icon
            :svg="IconBriefCase"
            width="24"
            height="24"
            :fill="isActive ? 'black' : 'gray-darker'"
          />
        </template>

        <template #text>
          {{ trans('user.menu.my_organization') }}
        </template>
      </MenuItemGroup>
    </li>

    <li class="px-3">
      <Divider class="m-0" color="gray" />
    </li>

    <li v-if="isExpanded">
      <MenuAccountManagerContact
        class="mx-3"
        :account-manager="user!.account_manager"
      />
    </li>
  </MenuList>
</template>
